import { Teacher, Teachers } from "../models/Teacher"
import { Schedule, Schedules } from "../models/Schedule"
import { Lesson, Lessons } from "@/models/Lesson"
import { Name } from "@/models/Name"
import firebase from 'firebase/app'
import { Address } from "@/models/Address"

class TeacherRepository {
  constructor(client) {
    this.collection = client.collection('teachers')
    this.snapShotOptions = { serverTimestamps : 'estimate' }
  }

  async put(
    values
  ) {
    const timestamp = firebase.firestore.FieldValue.serverTimestamp()
    const docRef = await this.collection.doc(values.uuid)
    const teacher = new Teacher({
      uuid: values.uuid,
      name: new Name(
        values.personalName,
        values.familyName,
        values.flatPersonalName,
        values.flatFamilyName
      ),
      email: values.email,
      address: new Address(
        values.postalCode,
        values.prefecture,
        values.municipality,
        values.subAddress
      ),
      birthday: values.birthday,
      sex: values.sex,
      phone: values.phone,
      availableSchedules: new Schedules(
        values.availableSchedules.map(schedule => {
          return new Schedule(
            schedule.day,
            schedule.start,
            schedule.end
          )
        })
      ),
      expectedWage: values.expectedWage,
      nearestStation: values.nearestStation,
      university: values.university,
      inUniversity: values.inUniversity,
      selfIntroduction: values.selfIntroduction,
      studentIds: [],
      lessons: new Lessons([]),
      createdAt: timestamp,
      updatedAt: timestamp,
      active: true
    }
    )
    const object = teacher.toObject()
    docRef.set(object.data)
    const result = await docRef.get()
    const data = result.data(this.snapShotOptions)
    return createFromFireStoreData(values.uuid, data)
  }

  async find(uuid) {
    const result = await this.collection.doc(uuid).get()
    const data = result.data(this.snapShotOptions)
    if (data) {
      return createFromFireStoreData(uuid, data)
    }
  }

  async update(teacher) {
    teacher.updatedAt = firebase.firestore.FieldValue.serverTimestamp()
    const object = teacher.toObject()
    const docRef = await this.collection.doc(teacher.uuid)
    docRef.set(object.data)
    const result = await docRef.get()
    const newTeacher = createFromFireStoreData(teacher.uuid, result.data(this.snapShotOptions))
    return newTeacher
  }

  async list() {
    const snapShot = await this.collection.get()
    let arr = []
    snapShot.forEach(doc => {
      arr.push(createFromFireStoreData(doc.id, doc.data(this.snapShotOptions)))
    })
    return createTeachers(arr)
  }

  async query(condition) {
    const snapShot = await this.collection.where(condition.key, condition.operator, condition.value).get()
    let arr = []
    snapShot.forEach(doc => {
      arr.push(createFromFireStoreData(doc.id, doc.data()))
    })
    return createTeachers(arr)
  }

  async collect(uuids) {
    if (!(uuids instanceof Array)) {
      throw new TypeError(`'uuids' must be an instance of Array. Actual: ${typeof uuids}`)
    }
    const arr = await Promise.all(uuids.map(async uuid => {
      return await this.find(uuid)
    }))
    return createTeachers(arr)
  }

  async delete(teacher) {
    return await this.collection.doc(teacher.uuid).delete()
  }

}

const createFromFireStoreData = (id, data) => {
  return new Teacher({
    uuid: id,
    name: new Name(
      data.personalName,
      data.familyName,
      data.flatPersonalName,
      data.flatFamilyName
    ),
    email: data.email,
    address: new Address(
      data.postalCode,
      data.prefecture,
      data.municipality,
      data.subAddress
    ),
    birthday: data.birthday.toDate(),
    sex: data.sex,
    phone: data.phone,
    availableSchedules: Schedules.createFromObjects(data.availableSchedules),
    expectedWage: data.expectedWage,
    nearestStation: data.nearestStation,
    university: data.university,
    inUniversity: data.inUniversity,
    selfIntroduction: data.selfIntroduction,
    studentIds: data.studentIds,
    lessons: Lessons.createFromObjects(data.lessons),
    createdAt: data.createdAt.toDate(),
    updatedAt: data.updatedAt.toDate(),
    iconUrl: data.iconUrl,
    active: data.active
  }
  )
}

const createTeachers = (arr) => {
  if (!(arr instanceof Array)) {
    throw new TypeError(`'arr' must be an instance of Array. Actual: ${typeof arr}`)
  }
  return new Teachers(arr)
}

const containsStudentIdCondition = (studentId) => {
  return {
    key: 'studentIds',
    operator: 'array-contains',
    value: studentId
  }
}

const QueryCondition = {
  studentId: containsStudentIdCondition
}


export { TeacherRepository, QueryCondition }
